// type-specific renderers for service_banner and service_inventory
// evidence. mirrors SnmpEvidenceCards' Card/Field/Grid primitives so
// the bottom-sheet layout stays consistent across evidence types.
//
// banner evidence is supporting evidence only, neutral wording, no
// vulnerability framing

import React from 'react'
import type { EngagementEvidence } from '@/lib/engagementsApi'
import {
  extractServiceBanner,
  extractServiceInventory,
  isServiceBannerEvidence,
  isServiceInventoryEvidence,
} from './serviceEvidence'

interface Props {
  evidence: EngagementEvidence
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="text-xs text-slate-700 dark:text-slate-200 break-words">{value}</span>
  </div>
)

const Card: React.FC<{ title: string; children: React.ReactNode; show?: boolean }> = ({ title, children, show = true }) => {
  if (!show) return null
  return (
    <section className="border-t border-slate-100 first:border-t-0 px-4 py-3">
      <h3 className="text-[10px] uppercase tracking-wide text-slate-400 mb-2 font-semibold">{title}</h3>
      {children}
    </section>
  )
}

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-2 gap-x-3 gap-y-2">{children}</div>
)

const NeutralNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-2 text-[11px] text-slate-500 italic">{children}</p>
)

// ── service_banner ─────────────────────────────────────────────────────

const ServiceBannerView: React.FC<Props> = ({ evidence }) => {
  const f = extractServiceBanner(evidence.raw_json)

  const hasService = f.port != null || f.protocol || f.service || f.service_version
  const hasBanner  = f.banner || f.http_title || f.http_server || (f.http_headers && Object.keys(f.http_headers).length > 0)
  const hasTls     = f.tls_subject || f.tls_issuer || (f.tls_san && f.tls_san.length > 0) ||
                     f.tls_not_before || f.tls_not_after ||
                     f.tls_self_signed != null || f.tls_expired != null || f.tls_version

  return (
    <>
      <Card title="Service">
        <Grid>
          {f.host_ip          && <Field label="Host"     value={<span className="font-mono">{f.host_ip}</span>} />}
          {f.port != null     && <Field label="Port"     value={<span className="font-mono">{f.port}</span>} />}
          {f.protocol         && <Field label="Protocol" value={f.protocol} />}
          {f.service          && <Field label="Service"  value={f.service} />}
          {f.service_version  && <Field label="Version"  value={f.service_version} />}
        </Grid>
        {!hasService && (
          <p className="text-xs text-slate-400 italic">No service identification fields in this banner record.</p>
        )}
        <NeutralNote>Open service identified by banner grab. Supporting evidence only.</NeutralNote>
      </Card>

      {hasBanner && (
        <Card title="Banner / HTTP">
          {f.banner && (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Banner</div>
              <pre className="text-[11px] font-mono whitespace-pre-wrap bg-slate-50 dark:bg-slate-900/30 rounded p-2 text-slate-700 dark:text-slate-200 max-h-48 overflow-y-auto">
                {f.banner}
              </pre>
            </div>
          )}
          <Grid>
            {f.http_title  && <Field label="HTTP title"  value={f.http_title} />}
            {f.http_server && <Field label="Server"      value={f.http_server} />}
          </Grid>
          {f.http_headers && Object.keys(f.http_headers).length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Headers</div>
              <div className="space-y-0.5">
                {Object.entries(f.http_headers).map(([k, v]) => (
                  <div key={k} className="font-mono text-[11px] text-slate-700 dark:text-slate-200">
                    <span className="text-slate-500">{k}:</span> {String(v)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {hasTls && (
        <Card title="TLS certificate">
          <Grid>
            {f.tls_subject    && <Field label="Subject"    value={<span className="font-mono break-all">{f.tls_subject}</span>} />}
            {f.tls_issuer     && <Field label="Issuer"     value={<span className="font-mono break-all">{f.tls_issuer}</span>} />}
            {f.tls_version    && <Field label="TLS version" value={f.tls_version} />}
            {f.tls_not_before && <Field label="Not before" value={<span className="font-mono">{f.tls_not_before}</span>} />}
            {f.tls_not_after  && <Field label="Not after"  value={<span className="font-mono">{f.tls_not_after}</span>} />}
            {f.tls_self_signed != null && (
              <Field label="Self-signed" value={f.tls_self_signed ? 'Yes' : 'No'} />
            )}
            {f.tls_expired != null && (
              <Field
                label="Expired"
                value={
                  f.tls_expired
                    ? <span className="text-amber-700 dark:text-amber-400">Yes</span>
                    : 'No'
                }
              />
            )}
          </Grid>
          {f.tls_san && f.tls_san.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Subject alt names</div>
              <div className="flex flex-wrap gap-1">
                {f.tls_san.map(san => (
                  <span key={san} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                    {san}
                  </span>
                ))}
              </div>
            </div>
          )}
          <NeutralNote>Certificate metadata collected. No vulnerability inferred from presence alone.</NeutralNote>
        </Card>
      )}
    </>
  )
}

// ── service_inventory ──────────────────────────────────────────────────

const ServiceInventoryView: React.FC<Props> = ({ evidence }) => {
  const f = extractServiceInventory(evidence.raw_json)
  const services = f.services.filter(s => s.port != null)
  // sort by port for stable display
  services.sort((a, b) => (a.port ?? 0) - (b.port ?? 0))

  return (
    <>
      <Card title="Host">
        <Grid>
          {f.host_ip               && <Field label="Host"          value={<span className="font-mono">{f.host_ip}</span>} />}
          {f.device_classification && <Field label="Device hint"   value={f.device_classification} />}
        </Grid>
        <NeutralNote>Read-only inventory. Open services identified by banner grab.</NeutralNote>
      </Card>

      <Card title="Open services">
        {services.length === 0 ? (
          <p className="text-xs text-slate-500">No open services identified.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/30">
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="px-2 py-1 font-medium">Port</th>
                  <th className="px-2 py-1 font-medium">Protocol</th>
                  <th className="px-2 py-1 font-medium">Service</th>
                  <th className="px-2 py-1 font-medium">Version</th>
                  <th className="px-2 py-1 font-medium">Banner hint</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                {services.map((s, i) => (
                  <tr key={`${s.port}-${s.protocol}-${i}`}>
                    <td className="px-2 py-1 font-mono">{s.port ?? '—'}</td>
                    <td className="px-2 py-1">{s.protocol ?? '—'}</td>
                    <td className="px-2 py-1">{s.service ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500">{s.version ?? '—'}</td>
                    <td className="px-2 py-1 text-slate-500 truncate max-w-[16rem]" title={s.banner_hint ?? ''}>
                      {s.banner_hint ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {f.detected_services && (
        <Card title="Detected services">
          <p className="text-xs text-slate-700 dark:text-slate-200 whitespace-pre-wrap">{f.detected_services}</p>
        </Card>
      )}
    </>
  )
}

export const ServiceEvidenceCards: React.FC<Props> = ({ evidence }) => {
  if (isServiceBannerEvidence(evidence))    return <ServiceBannerView evidence={evidence} />
  if (isServiceInventoryEvidence(evidence)) return <ServiceInventoryView evidence={evidence} />
  return null
}
